import React, { useState, useEffect, useRef, useCallback } from "react";
import { BASE_URL } from "../../constants";
import AppSpinner from "../Spinner/AppSpinner";
import DataLoader from "../Spinner/DataLoader";
import { apiFetch } from "../../utils/apiFetch";
import PageInfoModal from "../PageInfoModal/PageInfoModal";
import ReloadButton from "../LoadingButtons/ReloadButton";
import SearchButton from "../LoadingButtons/SearchButton";

const TESTS_INFO_ITEMS = [
  {
    icon: "play-fill",
    text: '"Testi başlat" düyməsi qrup üçün aktiv test sessiyası açır — tələbələr öz cihazlarında testi görüb başlaya bilərlər.',
  },
  {
    icon: "stop-fill",
    text: '"Bitir" düyməsi aktiv test sessiyasını sonlandırır; yeni cavab qəbul edilmir.',
  },
  {
    icon: "table",
    text: '"Canlı nəticələr" cədvəlində hər tələbənin test statusunu (Başlanmayıb / Davam edir / Tamamlandı) real vaxtda izləyə bilərsiniz.',
  },
  {
    icon: "arrow-clockwise",
    text: "Yenilə düyməsi və ya hər 10 saniyədə bir avtomatik yenilənmə ilə canlı nəticələri əldə edə bilərsiniz.",
  },
  {
    icon: "person-lines-fill",
    text: '"Nəticələr" tabında sol siyahıdan tələbə seçin, tarix aralığı müəyyənləşdirin və "Göstər" düyməsinə klik edin.',
  },
  {
    icon: "eye-fill",
    text: 'Nəticələr cədvəlindəki hər sətirə və ya "Ətraflı" düyməsinə klik edərək hər sual üçün tələbənin cavabını görə bilərsiniz.',
  },
];

const TABS = [
  { id: "tests", label: "Testlər" },
  { id: "results", label: "Nəticələr" },
];

const formatTime = (seconds) => {
  const m = String(Math.floor(seconds / 60)).padStart(2, "0");
  const s = String(seconds % 60).padStart(2, "0");
  return `${m}:${s}`;
};

const calcDuration = (begin, end) => {
  if (!begin || !end) return "—";
  const diff = Math.round((new Date(end) - new Date(begin)) / 1000);
  if (diff < 0) return "—";
  const m = Math.floor(diff / 60);
  const s = diff % 60;
  return m > 0 ? `${m}dəq ${s}san` : `${s}san`;
};

const STATUS_CONFIG = {
  "not-started": {
    label: "Başlanmayıb",
    bg: "#eff6ff",
    color: "#1d4ed8",
    border: "#bfdbfe",
  },
  started: {
    label: "Davam edir",
    bg: "#fff7ed",
    color: "#9a3412",
    border: "#fb923c",
  },
  done: {
    label: "Tamamlandı",
    bg: "#f0fdf4",
    color: "#166534",
    border: "#bbf7d0",
  },
};

const today = new Date().toISOString().split("T")[0];
const weekAgo = new Date(new Date().setDate(new Date().getDate() - 7))
  .toISOString()
  .split("T")[0];

const Tests = ({ groupId, totalStudents = 0, onCellClick = () => {} }) => {
  const [activeTab, setActiveTab] = useState("tests");

  // --- Testlər tab ---
  const [quizStatus, setQuizStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef(null);
  const timerStartKey = groupId ? `quizStartTime_${groupId}` : null;

  // --- Quiz states polling ---
  const [quizStates, setQuizStates] = useState([]);
  const [quizStatesRefreshing, setQuizStatesRefreshing] = useState(false);
  const quizStatesIntervalRef = useRef(null);

  // --- Column filters ---
  const [filterStudent, setFilterStudent] = useState("");
  const [filterQuizName, setFilterQuizName] = useState("");

  // --- Nəticələr tab ---
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [dateRange, setDateRange] = useState({ begin: weekAgo, end: today });
  const [results, setResults] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [resultDetail, setResultDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const startTimer = () => {
    if (timerStartKey)
      localStorage.setItem(timerStartKey, Date.now().toString());
    setElapsed(0);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setElapsed((prev) => prev + 1), 1000);
  };

  const stopTimer = () => {
    clearInterval(timerRef.current);
    if (timerStartKey) localStorage.removeItem(timerStartKey);
  };

  const restoreTimer = useCallback(() => {
    const saved = timerStartKey ? localStorage.getItem(timerStartKey) : null;
    const initial = saved
      ? Math.max(0, Math.floor((Date.now() - Number(saved)) / 1000))
      : 0;
    setElapsed(initial);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setElapsed((prev) => prev + 1), 1000);
  }, [timerStartKey]);

  const fetchQuizStates = useCallback(async () => {
    try {
      const res = await apiFetch(
        `${BASE_URL}/quizzes/get-teacher-group-students-quiz-states/${groupId}`,
      );
      if (!res.ok) return;
      const data = await res.json();
      setQuizStates(data.quizStates || []);
    } catch (_) {}
  }, [groupId]);

  const refreshQuizStates = async () => {
    setQuizStatesRefreshing(true);
    await Promise.all([fetchQuizStates(), new Promise((r) => setTimeout(r, 600))]);
    setQuizStatesRefreshing(false);
  };

  const startQuizStatesPolling = useCallback(() => {
    fetchQuizStates();
    quizStatesIntervalRef.current = setInterval(fetchQuizStates, 10000);
  }, [fetchQuizStates]);

  const stopQuizStatesPolling = () => {
    clearInterval(quizStatesIntervalRef.current);
  };

  useEffect(() => {
    const timers = [timerRef, quizStatesIntervalRef];
    return () => timers.forEach((r) => clearInterval(r.current));
  }, []);

  useEffect(() => {
    if (!groupId) return;
    const fetchGroupStatus = async () => {
      try {
        const res = await apiFetch(`${BASE_URL}/teacher-groups/${groupId}`);
        if (!res.ok) return;
        const data = await res.json();
        const status = data.teacherGroup?.quizStatus;
        if (status) {
          setQuizStatus(status);
          if (status === "started") {
            restoreTimer();
            startQuizStatesPolling();
          }
        }
      } catch (_) {}
    };
    fetchGroupStatus();
  }, [groupId, startQuizStatesPolling, restoreTimer]);

  const changeQuizStatus = async (status) => {
    setLoading(true);
    try {
      const res = await apiFetch(
        BASE_URL + "/teacher-groups/change-group-quiz-status",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            groupId: Number(groupId),
            quizStatus: status,
          }),
        },
      );
      if (!res.ok) throw new Error("Sorğu uğursuz oldu.");
      setQuizStatus(status);
      if (status === "started") {
        startTimer();
        startQuizStatesPolling();
      } else {
        stopTimer();
        stopQuizStatesPolling();
        fetchQuizStates();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = useCallback(async () => {
    if (!groupId) return;
    setStudentsLoading(true);
    try {
      const res = await apiFetch(
        BASE_URL + "/teacher-groups/get-teacher-one-group-students",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ groupId: Number(groupId), status: "Aktiv" }),
        },
      );
      const data = await res.json();
      setStudents(data.teacherGroupStudents || []);
    } catch (err) {
      console.error(err);
    } finally {
      setStudentsLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  useEffect(() => {
    if (activeTab === "results" && students.length === 0) fetchStudents();
  }, [activeTab, fetchStudents, students.length]);

  const fetchResults = async (student = selectedStudent) => {
    if (!student) return;
    setResultsLoading(true);
    setSelectedResult(null);
    setResultDetail(null);
    try {
      const [res] = await Promise.all([
        apiFetch(BASE_URL + "/quizzes/get-student-quiz-results", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            contractId: student.contractId,
            beginDate: dateRange.begin,
            endDate: dateRange.end,
          }),
        }),
        new Promise((r) => setTimeout(r, 600)),
      ]);
      const data = await res.json();
      setResults(data.quizResults || []);
    } catch (err) {
      console.error(err);
    } finally {
      setResultsLoading(false);
    }
  };

  const fetchResultDetail = async (registerId) => {
    setDetailLoading(true);
    setResultDetail(null);
    try {
      const res = await apiFetch(`${BASE_URL}/quizzes/result/${registerId}`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setResultDetail(data);
    } catch (err) {
      console.error(err);
    } finally {
      setDetailLoading(false);
    }
  };

  const isStarted = quizStatus === "started";

  const activeStudentNames = new Set(
    students.map((st) => `${st.name} ${st.surname}`),
  );
  const activeQuizStates =
    students.length > 0
      ? quizStates.filter((qs) => activeStudentNames.has(qs.student))
      : quizStates;
  const filteredQuizStates = activeQuizStates.filter((qs) => {
    const studentMatch =
      filterStudent === "" ||
      (qs.student || "").toLowerCase().includes(filterStudent.toLowerCase());
    const quizMatch =
      filterQuizName === "" ||
      (qs.quizName || "").toLowerCase().includes(filterQuizName.toLowerCase());
    return studentMatch && quizMatch;
  });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 16,
        padding: "20px 24px",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        position: "relative",
      }}
    >
      <DataLoader show={loading || studentsLoading || resultsLoading || detailLoading} />

      {/* Tab naviqasiyası */}
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          alignSelf: "flex-start",
        }}
      >
        <PageInfoModal title="Testlər" items={TESTS_INFO_ITEMS} />
        <div
          style={{
            display: "flex",
            gap: 2,
            background: "#f3f4f6",
            borderRadius: 8,
            padding: 3,
          }}
        >
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                border: "none",
                borderRadius: 6,
                padding: "6px 18px",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.15s",
                background: activeTab === tab.id ? "#fff" : "transparent",
                color: activeTab === tab.id ? "var(--color-primary)" : "#6b7280",
                boxShadow:
                  activeTab === tab.id ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        {/* ---- TESTLƏR TAB ---- */}
        {activeTab === "tests" && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 16,
              height: "100%",
              minHeight: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <button
                disabled={loading || isStarted}
                onClick={() => changeQuizStatus("started")}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  background: isStarted ? "#d1fae5" : "#059669",
                  color: isStarted ? "#6b7280" : "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "7px 16px",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: isStarted ? "not-allowed" : "pointer",
                  opacity: isStarted ? 0.6 : 1,
                  transition: "all 0.15s",
                }}
              >
                {loading && !isStarted ? (
                  <AppSpinner inline />
                ) : (
                  <>
                    <i className="bi bi-play-fill"></i>Testi başlat
                  </>
                )}
              </button>
              <button
                disabled={loading || !isStarted}
                onClick={() => changeQuizStatus("finished")}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  background: !isStarted ? "#fee2e2" : "#ef4444",
                  color: !isStarted ? "#6b7280" : "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "7px 16px",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: !isStarted ? "not-allowed" : "pointer",
                  opacity: !isStarted ? 0.6 : 1,
                  transition: "all 0.15s",
                }}
              >
                {loading && isStarted ? (
                  <AppSpinner inline />
                ) : (
                  <>
                    <i className="bi bi-stop-fill"></i>Bitir
                  </>
                )}
              </button>
              {quizStatus && (
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: isStarted ? "#059669" : "#6b7280",
                    background: isStarted ? "rgba(5,150,105,0.08)" : "#f3f4f6",
                    border: `1px solid ${isStarted ? "#059669" : "#d1d5db"}`,
                    borderRadius: 20,
                    padding: "3px 12px",
                  }}
                >
                  {isStarted ? "Test aktiv" : "Test bitdi"}
                </span>
              )}
            </div>

            {quizStatus && (
              <div style={{ display: "inline-flex", gap: 6, flexWrap: "wrap" }}>
                <div
                  style={{
                    background: isStarted ? "rgba(5,150,105,0.06)" : "#f9fafb",
                    border: `1px solid ${isStarted ? "#059669" : "#e5e7eb"}`,
                    borderRadius: 10,
                    padding: "14px 20px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 4,
                    minWidth: 140,
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#9ca3af",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Keçən vaxt
                  </span>
                  <span
                    style={{
                      fontSize: 32,
                      fontWeight: 800,
                      letterSpacing: 2,
                      color: isStarted ? "#059669" : "#6b7280",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {formatTime(elapsed)}
                  </span>
                  <span style={{ fontSize: 11, color: "#9ca3af" }}>
                    dəq : san
                  </span>
                </div>
              </div>
            )}

            {quizStatus && activeQuizStates.length > 0 && (
              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  minHeight: 0,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "7px 12px",
                    background: "#f9fafb",
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  <span
                    style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}
                  >
                    Canlı nəticələr
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: 11,
                        color: "#9ca3af",
                        fontWeight: 400,
                      }}
                    >
                      hər 10 san. yenilənir
                    </span>
                  </span>
                  <ReloadButton onClick={refreshQuizStates} loading={quizStatesRefreshing} />
                </div>
                <div
                  className="purple-scroll"
                  style={{
                    overflowX: "auto",
                    overflowY: "auto",
                    flex: 1,
                    minHeight: 0,
                  }}
                >
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 12,
                    }}
                  >
                    <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                      <tr style={{ background: "#f3f4f6" }}>
                        {[
                          "Quiz ID",
                          "Tələbə",
                          "Quiz Adı",
                          "Sual",
                          "Düzgün",
                          "Səhv",
                          "Status",
                          "Başlama",
                          "Bitmə",
                          "Müddət",
                        ].map((h, i) => (
                          <th
                            key={i}
                            style={{
                              padding:
                                i === 1 || i === 2
                                  ? "6px 12px 4px"
                                  : "8px 12px",
                              textAlign: i >= 3 && i <= 5 ? "center" : "left",
                              borderBottom: "1px solid #e5e7eb",
                              color: "#374151",
                              fontWeight: 700,
                              fontSize: 11,
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                              whiteSpace: "nowrap",
                              background: "#f3f4f6",
                            }}
                          >
                            {i === 1 ? (
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 4,
                                }}
                              >
                                <span>{h}</span>
                                <input
                                  value={filterStudent}
                                  onChange={(e) =>
                                    setFilterStudent(e.target.value)
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                  placeholder="Axtar..."
                                  style={{
                                    width: "100%",
                                    minWidth: 100,
                                    border: "1px solid #ddd6fe",
                                    borderRadius: 4,
                                    padding: "2px 6px",
                                    fontSize: 11,
                                    fontWeight: 400,
                                    color: "#111827",
                                    outline: "none",
                                    background: "#fff",
                                    textTransform: "none",
                                    letterSpacing: 0,
                                  }}
                                />
                              </div>
                            ) : i === 2 ? (
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 4,
                                }}
                              >
                                <span>{h}</span>
                                <input
                                  value={filterQuizName}
                                  onChange={(e) =>
                                    setFilterQuizName(e.target.value)
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                  placeholder="Axtar..."
                                  style={{
                                    width: "100%",
                                    minWidth: 120,
                                    border: "1px solid #ddd6fe",
                                    borderRadius: 4,
                                    padding: "2px 6px",
                                    fontSize: 11,
                                    fontWeight: 400,
                                    color: "#111827",
                                    outline: "none",
                                    background: "#fff",
                                    textTransform: "none",
                                    letterSpacing: 0,
                                  }}
                                />
                              </div>
                            ) : (
                              h
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredQuizStates.length === 0 && (
                        <tr>
                          <td
                            colSpan={10}
                            style={{
                              padding: "16px 12px",
                              textAlign: "center",
                              color: "#9ca3af",
                              fontSize: 12,
                            }}
                          >
                            Nəticə tapılmadı
                          </td>
                        </tr>
                      )}
                      {[...filteredQuizStates]
                        .sort((a, b) => {
                          const order = {
                            done: 0,
                            started: 1,
                            "not-started": 2,
                          };
                          return (
                            (order[a.status] ?? 3) - (order[b.status] ?? 3)
                          );
                        })
                        .map((qs, idx) => {
                          const cfg =
                            STATUS_CONFIG[qs.status] ||
                            STATUS_CONFIG["not-started"];
                          return (
                            <tr
                              key={qs.id}
                              style={{
                                borderBottom: "1px solid #e5e7eb",
                                background: idx % 2 === 0 ? "#fff" : "#fafafa",
                              }}
                            >
                              <td
                                style={{
                                  padding: "7px 12px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <span
                                  style={{
                                    background: "#eff6ff",
                                    color: "var(--color-primary)",
                                    padding: "2px 8px",
                                    borderRadius: 4,
                                    fontWeight: 700,
                                    fontSize: 11,
                                    border: "1px solid #bfdbfe",
                                  }}
                                >
                                  #{qs.quizId}
                                </span>
                              </td>
                              <td
                                style={{
                                  padding: "7px 12px",
                                  color: "#111827",
                                  fontWeight: 600,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {qs.student}
                              </td>
                              <td
                                style={{
                                  padding: "7px 12px",
                                  color: "#374151",
                                  whiteSpace: "nowrap",
                                  maxWidth: 200,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {qs.quizName}
                              </td>
                              <td
                                style={{
                                  padding: "7px 12px",
                                  textAlign: "center",
                                  color: "#374151",
                                  fontWeight: 600,
                                }}
                              >
                                {qs.questionCount}
                              </td>
                              <td
                                style={{
                                  padding: "7px 12px",
                                  textAlign: "center",
                                  color: "#059669",
                                  fontWeight: 700,
                                }}
                              >
                                {qs.correctCount}
                              </td>
                              <td
                                style={{
                                  padding: "7px 12px",
                                  textAlign: "center",
                                  color: "#ef4444",
                                  fontWeight: 700,
                                }}
                              >
                                {qs.incorrectCount}
                              </td>
                              <td
                                style={{
                                  padding: "7px 12px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <span
                                  style={{
                                    background: cfg.bg,
                                    color: cfg.color,
                                    border: `1px solid ${cfg.border}`,
                                    borderRadius: 20,
                                    padding: "2px 10px",
                                    fontWeight: 600,
                                    fontSize: 11,
                                  }}
                                >
                                  {cfg.label}
                                </span>
                              </td>
                              <td
                                style={{
                                  padding: "7px 12px",
                                  color: "#6b7280",
                                  whiteSpace: "nowrap",
                                  fontSize: 11,
                                }}
                              >
                                {qs.beginDate || "—"}
                              </td>
                              <td
                                style={{
                                  padding: "7px 12px",
                                  color: "#6b7280",
                                  whiteSpace: "nowrap",
                                  fontSize: 11,
                                }}
                              >
                                {qs.endDate || "—"}
                              </td>
                              <td
                                style={{
                                  padding: "7px 12px",
                                  color: "#374151",
                                  whiteSpace: "nowrap",
                                  fontWeight: 600,
                                  fontSize: 11,
                                }}
                              >
                                {calcDuration(qs.beginDate, qs.endDate)}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ---- NƏTİCƏLƏR TAB ---- */}
        {activeTab === "results" && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              height: "100%",
              minHeight: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <input
                type="date"
                value={dateRange.begin}
                onChange={(e) =>
                  setDateRange((p) => ({ ...p, begin: e.target.value }))
                }
                style={{
                  border: "1px solid #d1d5db",
                  borderRadius: 6,
                  padding: "5px 10px",
                  fontSize: 13,
                  color: "#111827",
                  outline: "none",
                }}
              />
              <span style={{ color: "#9ca3af", fontSize: 13 }}>—</span>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) =>
                  setDateRange((p) => ({ ...p, end: e.target.value }))
                }
                style={{
                  border: "1px solid #d1d5db",
                  borderRadius: 6,
                  padding: "5px 10px",
                  fontSize: 13,
                  color: "#111827",
                  outline: "none",
                }}
              />
              <SearchButton
                onClick={() => fetchResults()}
                loading={resultsLoading}
                disabled={!selectedStudent}
                label="Göstər"
              />
              {!selectedStudent && (
                <span style={{ fontSize: 12, color: "#9ca3af" }}>
                  Tələbə seçin
                </span>
              )}
            </div>

            <div style={{ display: "flex", gap: 12, flex: 1, minHeight: 0 }}>
              <div
                style={{
                  width: 260,
                  flexShrink: 0,
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  overflow: "auto",
                }}
              >
                {studentsLoading ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <AppSpinner text="Yüklənir..." />
                  </div>
                ) : (
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 12,
                    }}
                  >
                    <thead>
                      <tr style={{ background: "#f3f4f6" }}>
                        <th
                          style={{
                            padding: "8px 10px",
                            textAlign: "left",
                            fontWeight: 700,
                            fontSize: 11,
                            color: "#374151",
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            borderBottom: "1px solid #e5e7eb",
                          }}
                        >
                          #
                        </th>
                        <th
                          style={{
                            padding: "8px 10px",
                            textAlign: "left",
                            fontWeight: 700,
                            fontSize: 11,
                            color: "#374151",
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            borderBottom: "1px solid #e5e7eb",
                          }}
                        >
                          Ad Soyad
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((st, idx) => {
                        const isSelected = selectedStudent?.id === st.id;
                        return (
                          <tr
                            key={st.id}
                            onClick={() => {
                              setSelectedStudent(st);
                              fetchResults(st);
                            }}
                            style={{
                              cursor: "pointer",
                              background: isSelected
                                ? "rgba(37,99,235,0.08)"
                                : idx % 2 === 0
                                  ? "#fff"
                                  : "#fafafa",
                              borderBottom: "1px solid #e5e7eb",
                              transition: "background 0.15s",
                            }}
                          >
                            <td
                              style={{
                                padding: "7px 10px",
                                color: "#9ca3af",
                                fontWeight: 600,
                              }}
                            >
                              {idx + 1}
                            </td>
                            <td
                              style={{
                                padding: "7px 10px",
                                color: isSelected ? "#1d4ed8" : "#111827",
                                fontWeight: isSelected ? 700 : 400,
                              }}
                            >
                              {st.name} {st.surname}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              <div
                style={{
                  flex: 1,
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  overflow: "auto",
                }}
              >
                {!selectedStudent ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      gap: 8,
                      color: "#9ca3af",
                      padding: 16,
                    }}
                  >
                    <i
                      className="bi bi-person-lines-fill"
                      style={{ fontSize: 36 }}
                    ></i>
                    <span style={{ fontSize: 13 }}>Tələbə seçin</span>
                  </div>
                ) : resultsLoading ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                      padding: 16,
                    }}
                  >
                    <AppSpinner text="Yüklənir..." />
                  </div>
                ) : results.length === 0 ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      gap: 8,
                      color: "#9ca3af",
                      padding: 16,
                    }}
                  >
                    <i className="bi bi-inbox" style={{ fontSize: 36 }}></i>
                    <span style={{ fontSize: 13 }}>Nəticə tapılmadı</span>
                  </div>
                ) : selectedResult ? (
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "10px 14px",
                        borderBottom: "1px solid #e5e7eb",
                        background: "#f9fafb",
                      }}
                    >
                      <button
                        onClick={() => {
                          setSelectedResult(null);
                          setResultDetail(null);
                        }}
                        style={{
                          background: "none",
                          border: "1px solid #d1d5db",
                          borderRadius: 6,
                          padding: "3px 10px",
                          fontSize: 12,
                          cursor: "pointer",
                          color: "#374151",
                          fontWeight: 600,
                        }}
                      >
                        ← Geri
                      </button>
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#111827",
                        }}
                      >
                        {selectedResult.quizName ||
                          `Quiz #${selectedResult.registerId}`}
                      </span>
                    </div>
                    {detailLoading ? (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          padding: 32,
                        }}
                      >
                        <AppSpinner text="Yüklənir..." />
                      </div>
                    ) : resultDetail ? (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            gap: 16,
                            padding: "10px 14px",
                            borderBottom: "1px solid #e5e7eb",
                            flexWrap: "wrap",
                          }}
                        >
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: "#374151",
                            }}
                          >
                            Ümumi: <strong>{resultDetail.questionCount}</strong>
                          </span>
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: "#059669",
                            }}
                          >
                            Düzgün: <strong>{resultDetail.correctCount}</strong>
                          </span>
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: "#ef4444",
                            }}
                          >
                            Səhv: <strong>{resultDetail.incorrectCount}</strong>
                          </span>
                        </div>
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            fontSize: 12,
                          }}
                        >
                          <thead>
                            <tr style={{ background: "#f3f4f6" }}>
                              {[
                                "#",
                                "Sual",
                                "Tələbənin cavabı",
                                "Düzgün cavab",
                                "Nəticə",
                              ].map((h, i) => (
                                <th
                                  key={i}
                                  style={{
                                    padding: "7px 12px",
                                    textAlign: i === 4 ? "center" : "left",
                                    borderBottom: "1px solid #e5e7eb",
                                    color: "#374151",
                                    fontWeight: 700,
                                    fontSize: 11,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.04em",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {(resultDetail.questionResults || []).map(
                              (q, i) => (
                                <tr
                                  key={q.questionId}
                                  style={{
                                    borderBottom: "1px solid #e5e7eb",
                                    background:
                                      i % 2 === 0 ? "#fff" : "#fafafa",
                                  }}
                                >
                                  <td
                                    style={{
                                      padding: "7px 12px",
                                      color: "#9ca3af",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {i + 1}
                                  </td>
                                  <td
                                    style={{
                                      padding: "7px 12px",
                                      color: "#111827",
                                    }}
                                  >
                                    {q.questionText}
                                  </td>
                                  <td
                                    style={{
                                      padding: "7px 12px",
                                      color: q.isCorrect
                                        ? "#059669"
                                        : "#ef4444",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {q.studentSelectedAnswerText || "—"}
                                  </td>
                                  <td
                                    style={{
                                      padding: "7px 12px",
                                      color: "#059669",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {q.correctAnswerText}
                                  </td>
                                  <td
                                    style={{
                                      padding: "7px 12px",
                                      textAlign: "center",
                                    }}
                                  >
                                    {q.isCorrect ? (
                                      <span
                                        style={{
                                          background: "#f0fdf4",
                                          color: "#166534",
                                          border: "1px solid #bbf7d0",
                                          borderRadius: 20,
                                          padding: "2px 10px",
                                          fontWeight: 700,
                                          fontSize: 11,
                                        }}
                                      >
                                        ✓
                                      </span>
                                    ) : (
                                      <span
                                        style={{
                                          background: "#fef2f2",
                                          color: "#991b1b",
                                          border: "1px solid #fecaca",
                                          borderRadius: 20,
                                          padding: "2px 10px",
                                          fontWeight: 700,
                                          fontSize: 11,
                                        }}
                                      >
                                        ✗
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              ),
                            )}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: 32,
                          gap: 8,
                          color: "#9ca3af",
                        }}
                      >
                        <i
                          className="bi bi-exclamation-circle"
                          style={{ fontSize: 32 }}
                        ></i>
                        <span style={{ fontSize: 13 }}>Məlumat yüklənmədi</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 12,
                    }}
                  >
                    <thead>
                      <tr style={{ background: "#f3f4f6" }}>
                        {[
                          "#",
                          "ID",
                          "Quiz Adı",
                          "Sual",
                          "Düzgün",
                          "Səhv",
                          "Başlama",
                          "Bitmə",
                          "Müddət",
                          "",
                        ].map((h, i) => (
                          <th
                            key={i}
                            style={{
                              padding: "8px 12px",
                              textAlign: i >= 3 && i <= 5 ? "center" : "left",
                              borderBottom: "1px solid #e5e7eb",
                              color: "#374151",
                              fontWeight: 700,
                              fontSize: 11,
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {results.map((r, i) => (
                        <tr
                          key={r.id}
                          onClick={() => {
                            setSelectedResult(r);
                            fetchResultDetail(r.id);
                          }}
                          style={{
                            borderBottom: "1px solid #e5e7eb",
                            background: i % 2 === 0 ? "#fff" : "#fafafa",
                            cursor: "pointer",
                            transition: "background 0.15s",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background =
                              "rgba(37,99,235,0.05)")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background =
                              i % 2 === 0 ? "#fff" : "#fafafa")
                          }
                        >
                          <td
                            style={{
                              padding: "7px 12px",
                              color: "#9ca3af",
                              fontWeight: 600,
                            }}
                          >
                            {i + 1}
                          </td>
                          <td style={{ padding: "7px 12px" }}>
                            <span
                              style={{
                                background: "#eff6ff",
                                color: "#1d4ed8",
                                padding: "2px 8px",
                                borderRadius: 4,
                                fontWeight: 700,
                                fontSize: 11,
                                border: "1px solid #bfdbfe",
                              }}
                            >
                              #{r.id}
                            </span>
                          </td>
                          <td
                            style={{
                              padding: "7px 12px",
                              color: "#111827",
                              fontWeight: 600,
                            }}
                          >
                            {r.quizName}
                          </td>
                          <td
                            style={{
                              padding: "7px 12px",
                              textAlign: "center",
                              color: "#374151",
                              fontWeight: 600,
                            }}
                          >
                            {r.questionCount}
                          </td>
                          <td
                            style={{
                              padding: "7px 12px",
                              textAlign: "center",
                              color: "#059669",
                              fontWeight: 700,
                            }}
                          >
                            {r.correctCount}
                          </td>
                          <td
                            style={{
                              padding: "7px 12px",
                              textAlign: "center",
                              color: "#ef4444",
                              fontWeight: 700,
                            }}
                          >
                            {r.incorrectCount}
                          </td>
                          <td
                            style={{
                              padding: "7px 12px",
                              color: "#6b7280",
                              fontSize: 11,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {r.beginDate || "—"}
                          </td>
                          <td
                            style={{
                              padding: "7px 12px",
                              color: "#6b7280",
                              fontSize: 11,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {r.endDate || "—"}
                          </td>
                          <td
                            style={{
                              padding: "7px 12px",
                              color: "#374151",
                              fontWeight: 600,
                              fontSize: 11,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {calcDuration(r.beginDate, r.endDate)}
                          </td>
                          <td style={{ padding: "7px 12px" }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedResult(r);
                                fetchResultDetail(r.id);
                              }}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                                background: "rgba(37,99,235,0.08)",
                                color: "#1d4ed8",
                                border: "1px solid #bfdbfe",
                                borderRadius: 6,
                                padding: "4px 10px",
                                fontSize: 11,
                                fontWeight: 700,
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <i
                                className="bi bi-eye-fill"
                                style={{ fontSize: 11 }}
                              ></i>
                              Ətraflı
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Tests;
